/**
 * `@aio/platform-form` — declarative field type definitions.
 *
 * The form engine is schema-first: callers pass a zod `schema` for
 * validation and a `fields` config for layout/UI. This file owns the
 * `FieldDef` discriminated union that drives the renderer dispatch.
 *
 * Conventions:
 * - Every variant is a member of {@link FieldDef}, discriminated by `type`.
 * - Common props (label, description, etc.) live on {@link BaseFieldDef}.
 * - `label` may be a literal string OR an i18n key — the renderer resolves
 *   keys at render time. Nothing in this file resolves them.
 * - `condition` receives the current form values and returns visibility.
 *   Hidden fields are not unmounted; they are not rendered. Their values
 *   stay in the form state and stay validated unless callers strip them.
 */

/**
 * Form-values shape passed to `condition`. We accept the loosest shape
 * (`Record<string, unknown>`) so a single field config can be reused
 * across forms with different data shapes — the renderer narrows via
 * `useWatch` at runtime.
 */
export type FormValues = Record<string, unknown>;

/**
 * One option entry in `select`, `multiSelect`, `radio`, and `relation`
 * search results. `value` is the persisted value; `label` is what the
 * user sees (string OR i18n key — the renderer resolves it).
 */
export interface FieldOption {
  /** The persisted value (must be string for select/radio compatibility). */
  value: string;
  /** Display label OR i18n key. */
  label: string;
}

/**
 * Canonical field-type discriminator. The set is intentionally a closed
 * union: every renderer dispatches via exhaustive switch over this. Adding
 * a new field type means adding a member here AND a per-type FieldDef
 * AND a renderer case.
 */
export type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "money"
  | "percent"
  | "date"
  | "select"
  | "multiSelect"
  | "checkbox"
  | "switch"
  | "radio"
  | "tags"
  | "file"
  | "repeater"
  | "relation";

/**
 * Common props shared by every field variant. `TValue` is informational
 * — runtime never inspects it; it exists so per-type definitions can
 * surface their persisted value type to TypeScript callers if they want
 * to introspect a field's value shape.
 *
 * Implementation note for `exactOptionalPropertyTypes: true`: every
 * optional prop here is declared with `?:` and a non-undefined type.
 * Callers must omit the prop entirely rather than passing `undefined`.
 */
export interface BaseFieldDef<TValue> {
  /** Stable form-state key. Becomes the `name` register'd with rhf. */
  name: string;
  /** Display label OR i18n key. Resolved at render time. */
  label: string;
  /** Helper text below the label. */
  description?: string;
  /** Placeholder text for inputs that support it. */
  placeholder?: string;
  /** Whether the field is non-editable. Hides the field from tab order. */
  disabled?: boolean;
  /**
   * RBAC gate. The form reads this at render time and may hide the
   * field if the current user lacks the permission. Enforcement is
   * caller-driven; the engine just exposes the metadata.
   */
  requiredPermission?: string;
  /**
   * Conditional visibility. Receives the current form values; return
   * `true` to render the field, `false` to omit it. When omitted (the
   * default), the field is always visible.
   */
  condition?: (values: FormValues) => boolean;
  /**
   * Phantom marker so per-type defs can carry a value-type. Never
   * referenced at runtime; the underscore prefix matches the project's
   * unused-vars eslint convention.
   */
  readonly _value?: TValue;
}

/** Plain-text input. Persisted as `string`. */
export interface TextFieldDef extends BaseFieldDef<string> {
  type: "text";
  /** HTML autocomplete hint (e.g. `"organization"`, `"email"`). */
  autoComplete?: string;
  /** Maximum character length (HTML maxlength). */
  maxLength?: number;
}

/** Multi-line text input. Persisted as `string`. */
export interface TextareaFieldDef extends BaseFieldDef<string> {
  type: "textarea";
  /** Visible row count for the textarea. Defaults to 3 in the renderer. */
  rows?: number;
  /** Maximum character length. */
  maxLength?: number;
}

/** Generic numeric input. Persisted as `number`. */
export interface NumberFieldDef extends BaseFieldDef<number> {
  type: "number";
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Currency input. Persisted as `number` (the major-unit decimal, e.g.
 * `12.50`). The renderer formats the displayed value via the locale
 * formatters; the form state stays canonical.
 */
export interface MoneyFieldDef extends BaseFieldDef<number> {
  type: "money";
  /** ISO-4217 currency code (e.g. `"EUR"`, `"RSD"`). */
  currency: string;
}

/**
 * Percent input.
 *
 * STORAGE CONVENTION: persisted as the **raw number entered** by the user.
 * When a user types `15`, the stored value is `15` — meaning "15 %", not
 * `0.15`. zod schemas should declare `z.number().min(0).max(100)` (or a
 * tighter range that fits the use case) to enforce that meaning. If a
 * caller needs the historic decimal convention (0.15) for a backend
 * contract, they wrap the field in a zod `.transform()` rather than the
 * engine carrying both interpretations.
 *
 * Why raw rather than decimal: the simpler approach removes the
 * impedance mismatch between what the user sees in the input and what is
 * committed to form state, eliminates round-trip rounding artefacts, and
 * lets zod's range validators read naturally.
 */
export interface PercentFieldDef extends BaseFieldDef<number> {
  type: "percent";
  /** Number of decimal places to show. Defaults to 2. */
  decimals?: number;
}

/**
 * Calendar-date input. Persisted as ISO date string (`YYYY-MM-DD`).
 * Min/max are inclusive ISO strings.
 */
export interface DateFieldDef extends BaseFieldDef<string> {
  type: "date";
  /** Minimum selectable ISO date. */
  min?: string;
  /** Maximum selectable ISO date. */
  max?: string;
}

/** Single-select dropdown. Persisted as the option `value` (string). */
export interface SelectFieldDef extends BaseFieldDef<string> {
  type: "select";
  options: FieldOption[];
}

/** Multi-select combobox. Persisted as `string[]` of option values. */
export interface MultiSelectFieldDef extends BaseFieldDef<string[]> {
  type: "multiSelect";
  options: FieldOption[];
}

/** Boolean checkbox. Persisted as `boolean`. */
export interface CheckboxFieldDef extends BaseFieldDef<boolean> {
  type: "checkbox";
}

/** Boolean switch. Persisted as `boolean`. */
export interface SwitchFieldDef extends BaseFieldDef<boolean> {
  type: "switch";
}

/** Single-choice radio group. Persisted as the option `value` (string). */
export interface RadioFieldDef extends BaseFieldDef<string> {
  type: "radio";
  options: FieldOption[];
}

/**
 * Free-form tags input. Persisted as `string[]`. Has no preset options;
 * callers add tags by typing + Enter / comma.
 */
export interface TagsFieldDef extends BaseFieldDef<string[]> {
  type: "tags";
}

/**
 * Single-file upload. Persisted as `File | null`. The engine produces a
 * File object in form state; uploading the bytes is the caller's job
 * inside `onSubmit`.
 */
export interface FileFieldDef extends BaseFieldDef<File | null> {
  type: "file";
  /** MIME type filter (e.g. `"application/pdf"`, `"image/*"`). */
  accept?: string;
  /** Maximum byte size. Validated client-side; not a guarantee. */
  maxSize?: number;
}

/**
 * Repeating subform. Persisted as an array of objects whose shape is
 * dictated by the nested `fields` config.
 *
 * The `fields` array is `FieldDef[]` — a repeater can contain any field
 * type, including another repeater. Recursion depth is unbounded but
 * deeply nested repeaters are a UX anti-pattern; the engine doesn't
 * police it.
 */
export interface RepeaterFieldDef extends BaseFieldDef<Record<string, unknown>[]> {
  type: "repeater";
  /** Per-row field config. */
  fields: FieldDef[];
  /** Initial row count when the form first mounts with no value. */
  initialRows?: number;
}

/**
 * Async relation picker. The caller provides a `search` function that
 * resolves to candidate options for the current query. Persisted as the
 * selected option `value` (typically the related entity id).
 *
 * `getDisplayLabel` is the hydration escape hatch: when the form mounts
 * with a saved value and there's no in-memory search result for that
 * value (e.g. editing an existing record), the field calls this resolver
 * once on mount to populate the chip's display label. Without it the
 * chip falls back to rendering the raw value (e.g. an opaque id), which
 * works but reads poorly. Implementations typically point at the same
 * detail endpoint the rest of the app uses for that entity.
 */
export interface RelationFieldDef extends BaseFieldDef<string | null> {
  type: "relation";
  /** Async search resolver. Called on each query change after debounce. */
  search: (query: string) => Promise<FieldOption[]>;
  /**
   * Optional async resolver that returns a display label for an
   * already-persisted value. Used to hydrate the chip when the form
   * loads with a saved selection but no recent search result.
   */
  getDisplayLabel?: (value: string) => Promise<string>;
}

/**
 * Discriminated union of every supported field variant. The renderer
 * exhausts over `field.type` to dispatch.
 */
export type FieldDef =
  | TextFieldDef
  | TextareaFieldDef
  | NumberFieldDef
  | MoneyFieldDef
  | PercentFieldDef
  | DateFieldDef
  | SelectFieldDef
  | MultiSelectFieldDef
  | CheckboxFieldDef
  | SwitchFieldDef
  | RadioFieldDef
  | TagsFieldDef
  | FileFieldDef
  | RepeaterFieldDef
  | RelationFieldDef;

/**
 * Identity helper. Exists purely so callers get a typed `FieldDef[]`
 * without spelling the array shape at every site, mirroring
 * `defineColumns` from `@aio/platform-table`.
 *
 * The generic `T` is informational — the engine doesn't (yet) verify
 * that field `name`s match keys of `T`. A future iteration may tighten
 * this with a mapped type, but the union of value shapes makes a strict
 * key-of constraint difficult without losing discriminator narrowing.
 */
export function defineFields<_T = FormValues>(fields: FieldDef[]): FieldDef[] {
  return fields;
}
